/**
 * Sección "Seguridad" del panel de Superadmin: activar la verificación en
 * dos pasos (2FA por TOTP, Google Authenticator u otra app) de la PROPIA
 * cuenta. Se usa desde pages/Superadmin.jsx, con el token de superadmin.
 *
 * Alta en dos pasos, igual que en el backend: "Activar" pide una clave nueva
 * (POST /api/superadmin/2fa/generar → QR + clave en texto), pero el 2FA
 * recién queda activo cuando se confirma con un código real de la app
 * (POST /api/superadmin/2fa/activar) — así no se puede activar un 2FA que no
 * se llegó a configurar bien en el teléfono.
 *
 * La clave y el QR viven solo en el estado de este componente: no se guardan
 * en localStorage ni en ningún otro lado, y se descartan al cancelar, al
 * confirmar o al cerrar sesión.
 *
 * Mismo estilo visual que Superadmin.jsx / Login.jsx (login.css).
 */

import React, { useState } from 'react';
import axios from 'axios';
import { IconCandado, IconExito, IconAdvertencia } from './icons';
import '../styles/login.css';

const API_URL = process.env.REACT_APP_API_URL;

// La clave TOTP viene de corrido (32 caracteres); agrupada de a 4 se lee y
// se tipea mucho mejor. Las apps ignoran los espacios.
const agruparClave = (clave) => (clave || '').replace(/(.{4})/g, '$1 ').trim();

const SuperadminSeguridad = ({ token, activo, onCambio, onSesionExpirada }) => {
  const [configurando, setConfigurando] = useState(false);
  const [config, setConfig] = useState(null); // { secret, qr_data_url }
  const [codigo, setCodigo] = useState('');
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState(null); // { tipo: 'exito' | 'error', texto }

  const headers = { Authorization: `Bearer ${token}` };

  const manejarError = (err, textoPorDefecto) => {
    if (err.response?.status === 401) {
      // Sesión de superadmin vencida: la maneja el panel (vuelve al login).
      onSesionExpirada();
      return;
    }
    setMensaje({ tipo: 'error', texto: err.response?.data?.mensaje || textoPorDefecto });
  };

  const empezar = async () => {
    setMensaje(null);
    setCargando(true);
    try {
      const { data } = await axios.post(`${API_URL}/superadmin/2fa/generar`, {}, { headers });
      setConfig({ secret: data.secret, qr_data_url: data.qr_data_url });
      setCodigo('');
      setConfigurando(true);
    } catch (err) {
      console.error('Error al generar la clave de 2FA:', err);
      manejarError(err, 'No se pudo generar la clave. Probá de nuevo.');
      // Si el backend dice que ya estaba activo, el panel se resincroniza.
      if (err.response?.status === 409) await onCambio();
    } finally {
      setCargando(false);
    }
  };

  const cancelar = () => {
    setConfigurando(false);
    setConfig(null);
    setCodigo('');
    setMensaje(null);
  };

  const confirmar = async (e) => {
    e.preventDefault();
    if (codigo.length !== 6) return;
    setMensaje(null);
    setCargando(true);
    try {
      await axios.post(`${API_URL}/superadmin/2fa/activar`, { codigo }, { headers });
      setConfigurando(false);
      setConfig(null);
      setCodigo('');
      setMensaje({ tipo: 'exito', texto: 'Listo: la verificación en dos pasos quedó activada.' });
      await onCambio();
    } catch (err) {
      console.error('Error al activar el 2FA:', err);
      manejarError(err, 'No se pudo activar. Probá de nuevo.');
      setCodigo('');
    } finally {
      setCargando(false);
    }
  };

  const caja = mensaje && (
    <div style={cajaMensaje(mensaje.tipo)}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        {mensaje.tipo === 'exito'
          ? <IconExito size={16} stroke="currentColor" />
          : <IconAdvertencia size={16} stroke="currentColor" />}
      </span>
      <div>{mensaje.texto}</div>
    </div>
  );

  // --- ya activo ---
  if (activo) {
    return (
      <div style={estilos.contenedor}>
        {caja}
        <div style={cajaMensaje('exito')}>
          <span style={{ flexShrink: 0, marginTop: 1 }}><IconCandado size={16} stroke="currentColor" /></span>
          <div>
            <strong>Verificación en dos pasos: activada</strong>
            <div style={{ marginTop: 4, fontSize: 12 }}>
              Cada vez que ingreses, además de la contraseña se va a pedir el código de 6 dígitos de tu app autenticadora.
            </div>
          </div>
        </div>
        <span style={textoAyuda}>
          Si perdés el teléfono, se desactiva desde el servidor (por SSH) con{' '}
          <code>scripts/recuperar-2fa-superadmin.js</code>.
        </span>
      </div>
    );
  }

  // --- configurando: QR + clave + código ---
  if (configurando && config) {
    return (
      <div style={estilos.contenedor}>
        {caja}

        <div>
          <p style={subtituloSeccion}>1. Escaneá el código QR</p>
          <span style={textoAyuda}>
            Abrí Google Authenticator (o la app que uses), tocá “Agregar cuenta” y escaneá este código.
          </span>
          <div style={estilos.marcoQr}>
            <img
              src={config.qr_data_url}
              alt="Código QR para configurar la verificación en dos pasos"
              width={200}
              height={200}
              style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
            />
          </div>
        </div>

        <div>
          <p style={subtituloSeccion}>¿No podés escanearlo?</p>
          <span style={textoAyuda}>Cargá esta clave a mano en tu app:</span>
          <code style={estilos.clave}>{agruparClave(config.secret)}</code>
        </div>

        <form onSubmit={confirmar} className="login-form">
          <div className="form-group">
            <label htmlFor="codigo-activar">2. Código de 6 dígitos</label>
            <div className="input-wrapper">
              <span className="input-icon"><IconCandado size={16} stroke="currentColor" /></span>
              <input
                id="codigo-activar"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="form-input"
                style={estilos.inputCodigo}
                readOnly={cargando}
                autoFocus
              />
            </div>
            <span style={textoAyuda}>El que muestra la app ahora mismo (cambia cada 30 segundos).</span>
          </div>

          <button type="submit" className="btn-ingresar" disabled={cargando || codigo.length !== 6}>
            {cargando ? 'Confirmando...' : 'Confirmar'}
          </button>
          <button type="button" className="link-ayuda" onClick={cancelar} disabled={cargando} style={linkBoton}>
            Cancelar
          </button>
        </form>
      </div>
    );
  }

  // --- todavía desactivado ---
  return (
    <div style={estilos.contenedor}>
      {caja}
      <div style={cajaMensaje('aviso')}>
        <span style={{ flexShrink: 0, marginTop: 1 }}><IconCandado size={16} stroke="currentColor" /></span>
        <div>
          <strong>Verificación en dos pasos: desactivada</strong>
          <div style={{ marginTop: 4, fontSize: 12 }}>
            Además de la contraseña, se pide un código de 6 dígitos que genera una app en tu teléfono
            (Google Authenticator, Authy, etc.). Protege el acceso de nivel plataforma aunque alguien
            llegue a conocer la contraseña.
          </div>
        </div>
      </div>
      <button type="button" className="btn-ingresar" onClick={empezar} disabled={cargando}>
        {cargando ? 'Generando...' : 'Activar verificación en dos pasos'}
      </button>
    </div>
  );
};

// --- estilos puntuales de esta sección (el resto sale de login.css) ---
const subtituloSeccion = {
  margin: '0 0 6px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: '#7f8c8d',
  fontFamily: 'var(--font-header)',
};

const textoAyuda = {
  fontSize: 12,
  color: '#7f8c8d',
  lineHeight: 1.4,
};

const linkBoton = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 13,
  padding: 4,
};

const estilos = {
  contenedor: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  marcoQr: {
    display: 'flex',
    justifyContent: 'center',
    margin: '12px 0 0',
    padding: 12,
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
  },
  clave: {
    display: 'block',
    marginTop: 8,
    padding: '12px 14px',
    background: '#f8f9fa',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 14,
    letterSpacing: '1px',
    lineHeight: 1.6,
    color: '#2c3e50',
    textAlign: 'center',
    overflowWrap: 'anywhere',
    userSelect: 'all',
  },
  // El input de código va centrado y con letras espaciadas para que los 6
  // dígitos se lean de un vistazo (el padding izquierdo deja lugar al ícono).
  inputCodigo: {
    padding: '14px 14px 14px 40px',
    textAlign: 'center',
    letterSpacing: '8px',
    fontSize: 20,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
};

const cajaMensaje = (tipo) => {
  const colores = {
    exito: { fondo: 'rgba(0, 217, 163, 0.1)', color: '#0f8a6d', borde: '#00d9a3' },
    aviso: { fondo: 'rgba(243, 156, 18, 0.12)', color: '#9a6b00', borde: '#f39c12' },
    error: { fondo: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c', borde: '#e74c3c' },
  };
  const c = colores[tipo] || colores.aviso;
  return {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    background: c.fondo,
    color: c.color,
    padding: '12px 14px',
    borderRadius: 12,
    fontSize: 13,
    borderLeft: `4px solid ${c.borde}`,
    lineHeight: 1.45,
  };
};

export default SuperadminSeguridad;
